import { useState, useEffect, useCallback, useMemo } from "react";
import { COLOR_CODES } from "../../../common";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { BrushStart, LegendKey } from "../../../models/chart_model";
import { RajasthanDiscomDrawal } from "../../../models/rajasthan";
import { getRajasthanDiscomDrawlData } from "../Utility/RestApi";
import {
  formatDateForAPI,
  getLast7DaysDateRange,
  R_CHART_TYPES,
} from "../const";
import { GnaRuvnlFooter } from "../components";
import { DynamicDatePicker } from "../../../components/DatePicker";
import { convertToString } from "../../../extensions/date";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { FilterTab } from "../../../components/Filters";
import { DataTabs, DEMAND_UNITS } from "./Data";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector, useDispatch } from "react-redux";
import { ExchangeColors } from "../Exchange3/FormatData";
import { stringToConsistentColor } from "../../../extensions/string";
import { AddDatesWithFileName } from "../../../helpers/helper";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { RajasthanDrawalPeakAnalysis } from "./DrawalPeak";
import { RajasthanDrawalSummaryAnalysis } from "./DrawalSummary";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";

const GROUP_BY = [
  {
    label: "Area",
    key: "area",
    values: ["area", "discom"],
  },
  {
    label: "Discom",
    key: "discom",
    values: ["discom"],
  },
  {
    label: "District",
    key: "district",
    values: ["district", "district_only"],
  },
];

const DISCOM = [
  { label: "Jodhpur", key: "Jodhpur" },
  { label: "Jaipur", key: "Jaipur" },
  { label: "Ajmer", key: "Ajmer" },
];

let X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Discom/ Area",
    key: "area",
    isTime: false,
    values: ["area", "discom"],
  },
];

let Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = DEMAND_UNITS;

export function RajasthanDrawal() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const dispatch = useDispatch();

  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[2]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [data, setData] = useState<RajasthanDiscomDrawal[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<string[]>([]);
  const [uniqueArea, setUniqueArea] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([GROUP_BY[1]]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);
  const [selectedLegends, setSelectedLegends] = useState<LegendKey[]>([]);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    const dates = DefaultDates[activeTab].defaultRangeDate;
    setLoading(true);
    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);

    const newData = await getRajasthanDiscomDrawlData({
      startDate: startDateStr,
      endDate: endDateStr,
    });
    setData(newData);

    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item: any) => item.date)
            .sort(
              (a: any, b: any) => new Date(a).getTime() - new Date(b).getTime()
            )
        )
      )
    );
    setUniqueBlock(Array.from(new Set(newData.map((item: any) => item.block))));
    setLoading(false);
  }, [DefaultDates, activeTab]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys([GROUP_BY[index]]);
  }

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }

  const keys = useCallback(() => {
    if (dataTab.label === "Summary" || dataTab.label === "Table") {
      return selectedBlock.length > 0
        ? ["date", "block", "month"]
        : selectedInterval.values;
    }
    return selectedBlock.length > 0 ? ["date"] : selectedInterval.values;
  }, [dataTab.label, selectedBlock.length, selectedInterval.values]);

  useEffect(() => {
    let filteredData = data.map((item) => ({
      ...item,
      mwh: item.mw / 4,
      lu: item.mw / 400,
    }));

    if (selectedDiscom.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDiscom.includes(item.discom)
      );
    }

    filteredData = filteredData.map((item) => {
      return {
        ...item,
        district: item.district,
      };
    });

    if (selectedGroupKeys[0]?.key !== "district") {
      setUniqueArea(
        Array.from(
          new Set(
            filteredData.map((item) => item.area + " (" + item.discom + ")")
          )
        )
      );
    }

    if (selectedArea.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedArea
          .map((opt) => opt.value)
          .includes(item.area + " (" + item.discom + ")")
      );
    }
    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((opt) => opt.value).includes(item.date)
      );
    }
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((opt) => opt.value).includes(item.block)
      );
    }

    const par: ChartDataParams = {
      keys: keys(),
      tableKeys: ["date", "block", "month"],
      groupBy: selectedXaxis.isTime
        ? selectedGroupKeys
            .map((k) => k.key)
            .concat(selectedBlock.length > 0 ? ["block"] : [])
        : [],
      volumeKey: "",
      aggregationKeys: ["lu", "mwh", "mw"],
      averageKeys: ["mw"],
      priceKey: "",
      averageDuplicationKeys: ["block", "date"],
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedGroupKeys,
    selectedXaxis,
    selectedInterval,
    selectedDiscom,
    selectedArea,
    selectedDate,
    selectedBlock,
    dataTab.label,
    keys,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  function getLegends() {
    return chartKeys.map((key) => ({
      dataKey: key + "_" + selectedUnit.toLowerCase(),
      name: selectedXaxis.isTime ? key : "Volume Drawal",
    }));
  }

  function summaryExtraKeys() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.key === "month") {
          return ["month"];
        } else {
          return ["date", "block"];
        }
      } else {
        return ["date", "block"];
      }
    } else {
      return [selectedInterval.values[0]];
    }
  }

  function summaryExtraLabels() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.key === "month") {
          return ["Month"];
        } else {
          return ["Date", "Block"];
        }
      } else {
        return ["Date", "Block"];
      }
    } else {
      return [selectedInterval.values[0]];
    }
  }

  const tablePar = {
    legends: getLegends(),
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels(),
    filename: AddDatesWithFileName({
      fileName: "Restricted Demand",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
  };

  useEffect(() => {
    if (selectedLegends.length > 0) {
      const selectedLegendNames = selectedLegends.map((legend) => legend.name);

      let aa = [];
      let bb: any = [];
      for (let i = 0; i < selectedLegendNames.length; i++) {
        for (let j = 0; j < data.length; j++) {
          if (selectedLegendNames[i] === data[j].district) {
            aa.push(data[j]);
          }
        }
      }

      for (let i = 0; i < aa.length; i++) {
        if (bb.includes(aa[i].area + " (" + aa[i].discom + ")")) {
          continue;
        } else {
          bb.push(aa[i].area + " (" + aa[i].discom + ")");
        }
      }

      setUniqueArea(bb);
    }
  }, [data, selectedLegends]);

  const handleLegendSelect = (legends: LegendKey[]) => {
    setSelectedLegends(legends);
  };

  function getName(key: string) {
    if (selectedXaxis.isTime) {
      if (selectedGroupKeys[0].key === "district") {
        if (selectedArea.length > 0) {
          let aa = "";
          for (let i = 0; i < selectedArea.length; i++) {
            aa = aa + selectedArea[i].value + ", ";
          }
          return `${key + " - " + aa}`;
        } else {
          return key;
        }
      } else {
        return key;
      }
    } else {
      return "Volume Drawal";
    }
  }

  const handlexDataKey = useCallback(() => {
    if (selectedBlock.length > 0) {
      return "date";
    } else {
      if (selectedInterval.key === "time") {
        return "block";
      } else {
        return selectedInterval.key;
      }
    }
  }, [selectedBlock.length, selectedInterval.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function secondDataKey() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        return undefined;
      }
      return "date";
    } else {
      return undefined;
    }
  }

  // Show Peak Analysis component when selected
  if (selectedXAxisMode.value === "peak") {
    return (
      <RajasthanDrawalPeakAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
      />
    );
  }

  // Show Summary component when selected
  if (selectedXAxisMode.value === "summary") {
    return (
      <RajasthanDrawalSummaryAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        selectedGroupKeys={selectedGroupKeys}
        onGroupByChange={_handleGroupByChange}
      />
    );
  }

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />
          <FilterMenu
            title="Interval"
            items={Intervals.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => setSelectedInterval(Intervals[index])}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => handleUnitChange(item)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={setSelectedDiscom}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={selectedGroupKeys.map((item) => item.label)}
            onSelect={(item, index) => _handleGroupByChange(index)}
          />

          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(R_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">
              Restricted Demand ({selectedUnit})
            </h2>
            <div className="flex gap-x-6  justify-end">
              <div className="min-w-60">
                <SearchBox
                  options={uniqueArea.map((item) => ({
                    value: item,
                    label: item,
                  }))}
                  isMany={true}
                  placeholder="Search Area"
                  selectedOptions={selectedArea}
                  onChange={(v: any) => {
                    setSelectedArea(v);
                  }}
                />
              </div>
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedInterval.isTime}
              onLegendSelect={handleLegendSelect}
              onlyTitle={true}
              barCategoryGap={selectedXaxis.isTime ? 2 : 40}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={chartKeys.map((key, index) => {
                return {
                  dataKey: key + "_" + selectedUnit.toLowerCase(),
                  name: getName(key),
                  stroke: undefined,
                  type: selectedChartType.value,
                  stackId:
                    selectedChartType.stackId !== undefined
                      ? selectedChartType.stackId
                      : undefined,
                };
              })}
              xDataKey={xDataKey}
              secondXDataKey={secondDataKey()}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>GSS Report (RUVITL)</p>}
              text="1.	Sum of all GSS load = Actual drawal (Restricted demand) 
2.	MW = Represents the average (MW) when the selected time interval is Date or Month.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}